"use client";

import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, X } from "lucide-react";
import { useFilterContext } from "@/contexts/FilterContext";
import { DateTimeRangeModal } from "../modals/DateTimeRangeModal";
import { QuickFiltersPanel } from "./components/QuickFiltersPanel";
import { SimpleTableHeaderActiveFilters } from "./components/SimpleTable/SimpleTableHeaderActiveFilters";
import {
  useActiveFilterCount,
  useFilterActions,
} from "./components/SimpleTable/SimpleTableHeaderHooks";
import { useAdvancedFilterCallbacks } from "./hooks/callbacks/useAdvancedFilterCallbacks";
import { getQuickFilterFields } from "./utils/filter-rendering";
import type { SimpleTableConfig, BaseEntity } from "./types";

// ============================================================================
// TYPES
// ============================================================================

interface SimpleTableMobileSearchAndFiltersProps<T extends BaseEntity> {
  config: SimpleTableConfig<T>;
  mobileHeaderActions?: React.ReactNode;
}

// ============================================================================
// MOBILE SEARCH AND FILTERS COMPONENT
// ============================================================================

/**
 * Mobile search + filter bar.
 *
 * Renders the SAME filter set as the desktop toolbar: both surfaces build their
 * inputs from `QuickFiltersPanel` / `getQuickFilterFields`, so a filter added to
 * an entity config (user pickers, company/client pickers, datetime presets,
 * tags, booleans, option selects) shows up on phones automatically. Advanced
 * filters are reachable from the same panel.
 */
function SimpleTableMobileSearchAndFiltersInner<T extends BaseEntity>({
  config,
  mobileHeaderActions,
}: SimpleTableMobileSearchAndFiltersProps<T>) {
  const [showFilters, setShowFilters] = useState(false);
  const [filterMode, setFilterMode] = useState<"quick" | "advanced">("quick");

  // Use FilterContext directly — same core system as desktop SimpleTableHeader.
  const filterContext = useFilterContext<T>();
  const { filters, uiState, actions } = filterContext;
  const searchTerm = filters.search || "";
  const setSearchTerm = actions.setSearchTerm;

  const activeFilterCount = useActiveFilterCount<T>();
  const { handleClearAll, handleRemoveFilter } = useFilterActions<T>();
  const {
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
  } = useAdvancedFilterCallbacks<T>({ config });

  const hasQuickFilters = getQuickFilterFields<T>(config).length > 0;
  const filtersEnabled = Boolean(config.features?.filters) && hasQuickFilters;

  // Mirrors the desktop popover: only quick-filter fields are written back, so
  // filters owned by other surfaces are left untouched.
  const handleFiltersChange = useCallback(
    (newFilters: Record<string, unknown>) => {
      getQuickFilterFields<T>(config).forEach(({ id }) => {
        actions.setSimpleFilter(id, newFilters[id]);
      });
    },
    [config, actions]
  );

  return (
    // Sticky so search + filters stay reachable while scrolling a long list on
    // mobile. -mx-4/px-4 makes the background span the parent's p-4 padding edge-to-edge.
    <div className="sticky top-0 z-30 -mx-4 px-4 pt-2 pb-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/40 space-y-3">
      {/* Search Bar */}
      {config.features?.search && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={`Search ${config.displayName.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 w-full"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Filters Toggle and Actions Row */}
      {(filtersEnabled || mobileHeaderActions) && (
        <div className="flex items-center gap-2">
          {filtersEnabled && (
            <>
              <Button
                variant={showFilters ? "secondary" : "outline"}
                size="default"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 h-10"
                aria-expanded={showFilters}
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="default"
                  onClick={handleClearAll}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 h-10"
                >
                  Clear All
                </Button>
              )}
            </>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Mobile Header Actions - Aligned to right */}
          {mobileHeaderActions}
        </div>
      )}

      {/* Active Filters — same chip renderer (and name resolution) as desktop */}
      {activeFilterCount > 0 && (
        <SimpleTableHeaderActiveFilters
          config={config}
          searchValue=""
          simpleFilters={filters.simple}
          advancedFilters={filters.advanced}
          onRemoveSearch={() => setSearchTerm("")}
          onRemoveFilter={handleRemoveFilter}
          onRemoveAdvancedFilter={removeAdvancedFilter}
        />
      )}

      {/* Filters Panel */}
      {showFilters && filtersEnabled && (
        <div className="bg-card border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <QuickFiltersPanel<T>
            config={config}
            simpleFilters={filters.simple}
            onFiltersChange={handleFiltersChange}
            onCustomRangeOpen={handleCustomRangeClick}
            activeFilterCount={activeFilterCount}
            advancedFilterCount={filters.advanced.length}
            mode={filterMode}
            onModeChange={setFilterMode}
            gridClassName="grid-cols-1"
            advancedFilterProps={{
              advancedFilters: filters.advanced,
              filterOperator: uiState.filterOperator,
              setFilterOperator: actions.setFilterOperator,
              addAdvancedFilter,
              removeAdvancedFilter,
              clearAllAdvancedFilters,
            }}
          />

          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={handleClearAll} className="flex-1">
              Clear All
            </Button>
            <Button size="sm" onClick={() => setShowFilters(false)} className="flex-1">
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Custom datetime range picker — desktop renders this from the modals section */}
      <DateTimeRangeModal
        isOpen={uiState.customRangeModal.isOpen}
        onClose={handleCustomRangeClose}
        onApply={(start, end) => handleCustomRangeApply(start || new Date(), end || new Date())}
        fieldLabel={uiState.customRangeModal.fieldLabel || ""}
      />
    </div>
  );
}

// React.memo erases the generic call signature; re-assert it via `typeof Inner`
// so consumers can pass `SimpleTableConfig<TData>` without an `unknown` cast.
export const SimpleTableMobileSearchAndFilters = React.memo(
  SimpleTableMobileSearchAndFiltersInner
) as typeof SimpleTableMobileSearchAndFiltersInner;
